import { getBriefType_structuredBrief_fields } from '__generated__/getBriefType';
import Layout from 'projects/components/Layout';
import CreateBrief from 'projects/mutations/CreateBrief';
import BriefEntry from 'projects/routes/BriefEntry';
import PreziBrief from 'projects/routes/prezi/PreziBrief';
import ProjectBrief from 'projects/routes/project/ProjectBrief';
import SquareOnlineStoreBrief from 'projects/routes/squareOnlineStore/SquareOnlineStoreBrief';
import SquarespaceBrief from 'projects/routes/squarespace/SquarespaceBrief';
import * as React from 'react';
import { hot } from 'react-hot-loader';
import { useLocation } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
// Polyfill for IE11, can be dropped once we remove IE11 support
import 'url-search-params-polyfill';
import BriefRedirect from './BriefRedirect';
import { Packlane } from './Packlane';
import SquarespaceApiBrief from './SquarespaceApiBrief';

type BriefFieldsType = getBriefType_structuredBrief_fields['__typename'];

export interface BriefType {
    id: string;
    fields: { __typename: BriefFieldsType };
}

export const routes = {
    brief: (brief: BriefType, step: number = 1) => {
        const { id } = brief;

        switch (brief.fields.__typename) {
            case 'ProjectBriefFields':
                return `/briefs/projects/${id}/${step}`;
            case 'SquarespaceApiBriefFields':
                return `/squarespace/${id}`;
            case 'SquarespaceBriefFields':
                return `/briefs/squarespace/${id}/${step}`;
            case 'PreziBriefFields':
                return `/briefs/prezi/${id}/${step}`;
            case 'SquareOnlineStoreBriefFields':
                return `/briefs/square-online-store/${id}/${step}`;
            case 'PacklaneBriefFields':
                return `/briefs/packlane/${id}/${step}`;
            default:
                throw `Missing route for brief: ${brief.fields.__typename}`;
        }
    },
    project: (projectId: number) => `/projects/${projectId}`,
    yourWork: () => `/work`,
    projectCreate: () => `/projects/create`,
    support: () => `/help`,
};

export function redirectTo(url: string) {
    window.location.assign(url);
}

export function redirectToProject(projectId: number) {
    redirectTo(routes.project(projectId));
}

export function redirectToYourWork() {
    redirectTo(routes.yourWork());
}

export const redirectLaunchedBrief = (brief: { projectIds: number[] }) => {
    const { projectIds } = brief;
    projectIds.length == 1 ? redirectToProject(projectIds[0]) : redirectToYourWork();
};

export const redirectLaunchedStructuredBrief = (structuredBrief: { foreignKeys: number[] }) => {
    const { foreignKeys } = structuredBrief;
    foreignKeys.length == 1 ? redirectToProject(foreignKeys[0]) : redirectToYourWork();
};

export interface BriefFieldsRedirectProps {
    brief: BriefType;
    step?: number;
    push?: boolean;
}

export function BriefFieldsRedirect({ brief, step = 1, push = true }: BriefFieldsRedirectProps) {
    const { search } = useLocation();
    const url = routes.brief(brief, step);

    return <Redirect to={`${url}${search}`} push={push} />;
}

const BriefRoutes = () => (
    <Layout>
        <Switch>
            <Route path="/briefs/projects/create" component={BriefEntry} />
            <Route path={`/briefs/projects/:briefId/:step(1|2|3|4|5)`} component={ProjectBrief} />
            <Route
                path={`/briefs/projects/:briefId`}
                render={({ match }) => (
                    <BriefFieldsRedirect
                        brief={{
                            id: match.params.briefId,
                            fields: { __typename: 'ProjectBriefFields' },
                        }}
                    />
                )}
            />
            <Route
                path="/briefs/squarespace/create"
                render={() => <CreateBrief fields={{ squarespace: {} }} />}
            />
            <Route
                path={`/briefs/squarespace/:briefId/:step(1|2|3|4|5)`}
                component={SquarespaceBrief}
            />
            <Route
                path={`/briefs/squarespace/:briefId`}
                render={({ match }) => (
                    <BriefFieldsRedirect
                        brief={{
                            id: match.params.briefId,
                            fields: { __typename: 'SquarespaceBriefFields' },
                        }}
                    />
                )}
            />

            <Route
                path="/briefs/prezi/create"
                render={() => <CreateBrief fields={{ prezi: {} }} />}
            />
            <Route path={`/briefs/prezi/:briefId/:step(1|2|3|4|5)`} component={PreziBrief} />
            <Route
                path={`/briefs/prezi/:briefId`}
                render={({ match }) => (
                    <BriefFieldsRedirect
                        brief={{
                            id: match.params.briefId,
                            fields: { __typename: 'PreziBriefFields' },
                        }}
                    />
                )}
            />
            <Route
                path="/briefs/square-online-store/create"
                render={() => <CreateBrief fields={{ squareOnlineStore: {} }} />}
            />
            <Route
                path={`/briefs/square-online-store/:briefId/:step(1|2|3|4|5)`}
                component={SquareOnlineStoreBrief}
            />
            <Route
                path={`/briefs/square-online-store/:briefId`}
                render={({ match }) => (
                    <BriefFieldsRedirect
                        brief={{
                            id: match.params.briefId,
                            fields: { __typename: 'SquareOnlineStoreBriefFields' },
                        }}
                    />
                )}
            />

            <Route
                path="/briefs/packlane/create"
                render={() => <CreateBrief fields={{ packlane: {} }} />}
            />
            <Route path={`/briefs/packlane/:briefId/:step(1|2|3|4|5)`} component={Packlane} />
            <Route
                path={`/briefs/packlane/:briefId`}
                render={({ match }) => {
                    return (
                        <BriefFieldsRedirect
                            brief={{
                                id: match.params.briefId,
                                fields: { __typename: 'PacklaneBriefFields' },
                            }}
                        />
                    );
                }}
            />
        </Switch>
    </Layout>
);

const Routes = () => (
    <Switch>
        <Route exact path="/briefs/:briefId" component={BriefRedirect} />
        <Route path="/briefs" component={BriefRoutes} />
        <Route path="/squarespace/:briefId" component={SquarespaceApiBrief} />
    </Switch>
);

export default hot(module)(Routes);
